import { useEffect, useState } from 'react';
import { api } from '../api/endpoints';
import type { StationSession, Station } from '../api/types';
import { formatMinutes, formatMoney } from '../lib/format';
import { Ps5RatePicker, usePs5Rates } from './Ps5RatePicker';
import { useToast } from './Toast';
import { Button, ErrorNote, Modal } from './ui';

// The group on a PS5 wants to keep playing. The new booking stacks on top of
// the time they have left, and its price goes on the same customer's tab.
export function AddPs5TimeDialog({
  station,
  session,
  onClose,
  onAdded,
}: {
  station: Station;
  session: StationSession;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const choice = usePs5Rates(true);
  const { setPlayers } = choice;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same group size as they started with is the usual case.
  useEffect(() => {
    if (session.player_count !== null) setPlayers(session.player_count);
  }, [session.player_count, setPlayers]);

  const add = async () => {
    if (!choice.rate) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.sessions.extend(session.session_id, choice.rate.ps5_pricing_id);
      toast.success(
        `Added ${formatMinutes(result.added_minutes)} on ${result.station_name} — ${formatMoney(result.price)} on ${result.customer_name}'s tab. ${formatMinutes(result.minutes_remaining)} left in total.`,
      );
      onAdded();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add time on ${station.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-400">
          Stacks on top of the time {session.customer_name}'s group has left. The shorter
          booking is played first, and once the group starts playing on added time it can no
          longer be taken off the tab.
        </p>
        <Ps5RatePicker choice={choice} />
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex gap-2">
          <Button className="flex-1" disabled={busy || !choice.rate} onClick={add}>
            {busy ? 'Adding…' : 'Add time'}
          </Button>
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
